import React, { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useConfig } from "../../../../../../app/configcontext";
import { update_business_licence } from "../../../../../../features/business/businessSlice";
import ProtectedPaymentRoute from "../../../../../auth/Auth";

export default function Report() {
  ProtectedPaymentRoute();

  const navigate = useNavigate();
  const config = useConfig();
  const operationAgreementId = useSelector(
    (state) => state.business.operationAgreementId
  );
  const businessTypeId = useSelector((state) => state.business.businessTypeId);
  const licenseReportId = useSelector((state) => state.business.licenseReportId);
  const dispatch = useDispatch();
  const agreement = useMemo(() => config?.operationAgreement || [], [config?.operationAgreement]);
  const licenseReports = useMemo(() => {
    return (config?.licenseReport || []).filter(
      (item) =>
        item?.businessTypeId === undefined || item.businessTypeId === businessTypeId
    );
  }, [config?.licenseReport, businessTypeId]);

  const selectedAgreement = useMemo(
    () => agreement.find((item) => item.id === operationAgreementId) || null,
    [agreement, operationAgreementId]
  );

  const selectedReport = useMemo(() => {
    if (licenseReports.length === 0) return null;
    if (licenseReportId !== null && licenseReportId !== undefined) {
      return (
        licenseReports.find((item) => item.id === licenseReportId) ||
        licenseReports[0]
      );
    }
    return licenseReports[0];
  }, [licenseReports, licenseReportId]);

  const selectedPrice =
    selectedReport?.price ?? selectedAgreement?.price ?? 99;

  const clickNext = () => {
    const reportId = selectedReport?.id ?? 1;
    dispatch(update_business_licence(reportId));
    navigate("/bank_details");
  };

  const handleSkip = () => {
    dispatch(update_business_licence(null));
    navigate("/bank_details");
  };

  const backbtn = () => {
    navigate("/agreement");
  };

  return (
    <div className="min-h-[75vh] bg-white py-12 lg:py-20 font-sans">
      <div className="container mx-auto px-4 lg:px-8 max-w-4xl">
        
        {/* Header */}
        <div className="mb-10 text-center max-w-2xl mx-auto">
          <span className="text-[#1a73e8] font-medium tracking-wider uppercase text-xs mb-2 block">
            Licensing & Permit Package
          </span>
          <h1 className="text-3xl sm:text-4xl font-normal text-[#202124] tracking-tight mb-3">
            Secure the licenses & permits you need to run legally
          </h1>
          <p className="text-[15px] text-[#5f6368] leading-relaxed">
            We identify every federal, state, county, and local permit required for your specific industry and location to ensure complete legal compliance.
          </p>
        </div>

        {/* Report Card */}
        <div className="bg-white rounded-[16px] border border-[#dadce0] p-8 sm:p-10 shadow-sm max-w-2xl mx-auto mb-10">
          <div className="flex items-center justify-between border-b border-[#f1f3f4] pb-6 mb-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-[#e8f0fe] flex items-center justify-center text-[#1a73e8] text-xl">
                <i className="fa-solid fa-file-shield"></i>
              </div>
              <div>
                <h3 className="text-xl font-medium text-[#202124]">Business License Report</h3>
                <span className="text-xs text-[#5f6368]">Comprehensive multi-jurisdiction research</span>
              </div>
            </div>
            <div className="text-right">
              <span className="text-2xl font-bold text-[#202124]">${selectedPrice}</span>
              <span className="text-xs text-[#5f6368] block">one-time fee</span>
            </div>
          </div>

          <ul className="space-y-4 mb-8">
            <li className="flex items-start gap-3 text-[14.5px] text-[#3c4043]">
              <svg className="w-5 h-5 text-[#1e8e3e] flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
              <span>A customized summary of federal, state, county, and municipal licenses required for your business.</span>
            </li>
            <li className="flex items-start gap-3 text-[14.5px] text-[#3c4043]">
              <svg className="w-5 h-5 text-[#1e8e3e] flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
              <span>Clear guidance and breakdown on filing fees, renewal schedules, and governing agencies.</span>
            </li>
            <li className="flex items-start gap-3 text-[14.5px] text-[#3c4043]">
              <svg className="w-5 h-5 text-[#1e8e3e] flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
              <span>Direct application links and pre-filled instructions to accelerate your state approvals.</span>
            </li>
          </ul>

          <button
            onClick={clickNext}
            className="w-full py-3.5 rounded-[6px] bg-[#1a73e8] hover:bg-[#1557b0] !text-white font-medium shadow-sm transition-all text-[15px] cursor-pointer"
          >
            Add Business License Report (${selectedPrice})
          </button>
        </div>

        {/* Footer Actions */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-6 border-t border-[#f1f3f4] max-w-2xl mx-auto">
          <button
            type="button"
            onClick={backbtn}
            className="px-6 py-2.5 rounded-[4px] border border-[#dadce0] text-[#1a73e8] font-medium hover:bg-[#f8f9fa] transition-colors flex items-center gap-2 cursor-pointer text-[14px]"
          >
            <i className="fa-solid fa-arrow-left text-xs"></i> Back
          </button>
          <button
            type="button"
            onClick={handleSkip}
            className="text-[14px] text-[#5f6368] hover:text-[#1a73e8] underline transition-colors cursor-pointer bg-transparent border-none"
          >
            Skip license report for now
          </button>
        </div>

      </div>
    </div>
  );
}
